// Who am I, which workspace, which lines, who else works here. Cached for the session.

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type MeResponse, type UserSummary } from '../api';

export const ME_KEY = ['me'] as const;

export function useMe() {
  return useQuery<MeResponse, ApiError>({
    queryKey: ME_KEY,
    queryFn: api.auth.me,
    staleTime: 60_000,
    retry: (count, err) => err.status !== 401 && count < 2,
  });
}

/** Everyone in the workspace for the assign dropdown. Prefers /me.users; falls back to /users (admin). */
export function useUsers(): UserSummary[] {
  const me = useMe();
  const fromMe = me.data?.users;
  const q = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.users.list()).items,
    enabled: !!me.data && !fromMe,
    staleTime: 60_000,
    retry: false,
  });
  const meUser = me.data?.user;
  const list = q.data;
  return useMemo(() => {
    if (fromMe) return fromMe;
    if ((!list || list.length === 0) && meUser) return [{ id: meUser.id, name: meUser.name, role: meUser.role }];
    return (list ?? []).map((u) => ({ id: u.id, name: u.name, role: u.role, disabled_at: u.disabled_at }));
  }, [fromMe, list, meUser]);
}

export function useInvalidateMe() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ME_KEY });
}
